// src/components/employer/ViewAvailability.js
import { useState, useEffect } from 'react';
import { Container, Card, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { db } from '../../firebase/firebase';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const ViewAvailability = () => {
  const { userDetails } = useAuth();
  const [employeeAvailability, setEmployeeAvailability] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let unsubscribe;

    const fetchAvailability = async () => {
      if (!userDetails?.communityId) {
        console.log('No community ID available');
        return;
      }

      try {
        // First, get active users from the users collection
        const usersRef = collection(db, 'users');
        const usersQuery = query(
          usersRef,
          where('communityId', '==', userDetails.communityId),
          where('userType', '==', 'employee')
        );
        
        const userSnapshot = await getDocs(usersQuery);
        const activeUsers = new Map();
        
        userSnapshot.forEach(doc => {
          const userData = doc.data();
          // Use email as key to prevent duplicates
          activeUsers.set(userData.email, {
            userId: doc.id,
            ...userData
          });
        });

        console.log('Active users:', activeUsers.size);

        // Now set up listener for availability
        const availabilityRef = collection(db, 'availability');
        const q = query(
          availabilityRef,
          where('communityId', '==', userDetails.communityId)
        );

        unsubscribe = onSnapshot(q, (snapshot) => {
          const availabilityMap = new Map();
          
          snapshot.forEach((doc) => {
            const data = doc.data();
            // Only include availability if user is active
            if (activeUsers.has(data.userEmail)) {
              // Use email as key to prevent duplicates
              availabilityMap.set(data.userEmail, {
                ...data,
                userId: doc.id
              });
            }
          });

          // Convert map to array and sort by name
          const availabilityData = Array.from(availabilityMap.values())
            .sort((a, b) => a.userName.localeCompare(b.userName));

          console.log('Filtered availability data:', availabilityData.length);
          setEmployeeAvailability(availabilityData);
          setLoading(false);
        });

      } catch (error) {
        console.error('Error setting up availability:', error);
        setError('Failed to load employee availability');
        setLoading(false);
      }
    };

    fetchAvailability();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [userDetails]);

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const formatTime = (time) => {
    try {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch (error) {
      return time;
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Employee Availability</h4>
          <Badge bg="light" text="primary" className="px-3 py-2">
            {employeeAvailability.length} Active Employee{employeeAvailability.length !== 1 ? 's' : ''}
          </Badge>
        </Card.Header>
        <Card.Body className="p-0">
          {employeeAvailability.length === 0 ? (
            <Alert variant="info" className="m-3">
              No active employees have submitted their availability.
            </Alert>
          ) : (
            <div className="table-responsive">
              <Table bordered hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="text-nowrap">Employee Name</th>
                    {days.map(day => (
                      <th key={day} className="text-center text-capitalize">
                        {day}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employeeAvailability.map((employee) => (
                    <tr key={employee.userEmail}>
                      <td className="text-nowrap">
                        <div>{employee.userName}</div>
                        <small className="text-muted">{employee.userEmail}</small>
                      </td>
                      {days.map((day) => {
                        const dayData = employee.availability[day];
                        return (
                          <td key={day} className="text-center align-middle">
                            {dayData.isAvailable ? (
                              <Badge bg="success" className="px-2 py-1">
                                {formatTime(dayData.startTime)} - {formatTime(dayData.endTime)}
                              </Badge>
                            ) : (
                              <Badge bg="secondary" className="px-2 py-1">
                                Unavailable
                              </Badge>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ViewAvailability;